import type { ComponentProps, ReactNode } from 'react';

import { cx } from '@mage-ui/styled-system/css';
import {
  input,
  inputButton,
  inputDescription,
  inputError,
  inputLabel,
  inputRoot,
  inputSection,
  inputWrapper,
} from '@mage-ui/styled-system/recipes';
import { PasswordInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Icon } from '@/components/data-display';

export type InputPasswordProps = Omit<ComponentProps<'input'>, 'size'> & {
  name?: string;
  label?: string;
  description?: string;
  error?: string;
  classNames?: {
    root?: string;
    label?: string;
    description?: string;
    error?: string;
    wrapper?: string;
    input?: string;
    section?: string;
    inputButton?: string;
  };
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  value?: string | number;
};

export const InputPassword = ({
  classNames,
  startSlot,
  endSlot,
  ...props
}: InputPasswordProps) => {
  const [visible, { toggle }] = useDisclosure(false);

  return (
    <PasswordInput
      classNames={{
        root: cx(classNames?.root ?? inputRoot(), 'group'),
        label: classNames?.label ?? inputLabel(),
        required: classNames?.label ?? inputLabel(),
        description: classNames?.description ?? inputDescription(),
        error: classNames?.error ?? inputError(),
        wrapper: classNames?.wrapper ?? inputWrapper(),
        innerInput: cx(classNames?.input ?? input(), 'peer'),
        section: classNames?.section ?? inputSection(),
      }}
      visible={visible}
      onVisibilityChange={toggle}
      visibilityToggleIcon={(props) => (
        <Icon name={visible ? 'eye' : 'eye-off'} {...props} />
      )}
      rightSection={endSlot}
      visibilityToggleButtonProps={{
        className:
          classNames?.inputButton ??
          inputButton({ variant: 'ghost', size: 's' }),
      }}
      {...props}
    />
  );
};
